import * as _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';

const DEFAULT_DEBOUNCE_TIMEOUT = 500;

/**
 * Returns a `debouncedValue` that lags behind `value` by `debounceTimeout` ms — useful for
 * gating effects (e.g. a registry-search fetch) on a paused-typing stream rather than every
 * keystroke. Pairs well with externally-owned `value` (controlled components); for the
 * uncontrolled-input case where the hook should also own the typing state, see
 * `useDebouncedInputValue`, which composes this one.
 */
export const useDebouncedValue = <T>(value: T, debounceTimeout = DEFAULT_DEBOUNCE_TIMEOUT) => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  //  useMemo > useCallback here because useCallback expects an arrow function
  //  instead of the function returned by _.debounce.
  const onChangeDebouncedValue = useMemo(() =>
    _.debounce(setDebouncedValue, debounceTimeout), [debounceTimeout]);

  useEffect(() => {
    onChangeDebouncedValue(value);
  }, [value, onChangeDebouncedValue]);

  return { debouncedValue };
};
